import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { MixpanelEvents } from "@/constants/mixpanel-events";
import { validateChatImageBatch } from "@/features/ai-chat/utils/chat-image";
import { normalizePhotoResult } from "@/hooks/utils/use-photo-picker";
import { aiChatModalOpenAtom, pendingChatImageAtom } from "@/stores/chat.store";
import { trackMixpanelEvent } from "@/utils/mixpanel";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useSetAtom } from "jotai";
import { memo, useEffect } from "react";

// On Android, OEMs with aggressive memory management kill our process
// while the camera Activity is foregrounded. When the WebView cold-starts,
// Capacitor delivers the camera result via `appRestoredResult` so the JS
// layer can resume the flow. We use a localStorage flag set by
// usePhotoPicker to scope this listener to chat-initiated camera launches
// only — recipe-flow camera kills are out of scope for this PR.
export const ChatPhotoRestorationListener = memo(function ChatPhotoRestorationListener() {
  const setPendingImage = useSetAtom(pendingChatImageAtom);
  const openAiChatModal = useSetAtom(aiChatModalOpenAtom);

  useEffect(() => {
    if (Capacitor.getPlatform() === "web") return;

    const handlePromise = App.addListener("appRestoredResult", (data) => {
      const wasFromChat =
        localStorage.getItem(LocalStorageKeys.CHAT_CAMERA_IN_PROGRESS_KEY) === "1";
      localStorage.removeItem(LocalStorageKeys.CHAT_CAMERA_IN_PROGRESS_KEY);

      if (!wasFromChat) return;
      if (data.pluginId !== "Camera" || data.methodName !== "getPhoto") return;
      if (!data.success) return;

      void normalizePhotoResult(data.data ?? {})
        .then((picked) => {
          if (!picked) return;
          setPendingImage((previous) => {
            const next = [...previous, picked];
            try {
              validateChatImageBatch(next);
              return next;
            } catch {
              return previous;
            }
          });
          openAiChatModal(true);
          trackMixpanelEvent(MixpanelEvents.AI_CHAT_ATTACHMENT_RESTORED);
        })
        .catch(() => {});
    });

    return () => {
      void handlePromise.then((handle) => handle.remove());
    };
  }, [setPendingImage, openAiChatModal]);

  return null;
});
